import React, { useState } from "react";
import { Link } from "react-router-dom";
import PageShell from "@/components/PageShell";
import Lightbox from "@/components/Lightbox";
import { AuctionStatusPill, SampleTag, fmtDate } from "@/components/AuctionCard";
import { api, formatApiError } from "@/lib/api";
import { CONTACT, waLink } from "@/lib/brand";
import { toast } from "sonner";
import {
    Gavel, MapPin, PhoneCall, MessageCircle, ExternalLink, AlertTriangle, CalendarDays, Clock,
    Landmark, Wallet, KeyRound, FileText, Eye, Ruler, BedDouble, Check, Lock,
} from "lucide-react";

export function AuctionDisclaimer() {
    return (
        <div data-testid="auction-disclaimer" className="border-l-4 border-[var(--ebg-gold)] bg-[var(--ebg-gold)]/[0.07] p-5 md:p-6">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] font-bold text-black">
                <AlertTriangle size={14} className="text-[var(--ebg-gold)]"/> Auction Disclaimer
            </div>
            <p className="text-[13px] text-black/70 leading-relaxed mt-3">
                Auction properties shown here are based on public auction notices issued by banks, financial institutions,
                tribunals or government authorities, and are published by EBG Realty for information only. EBG Realty is not
                the seller or the auctioning authority and does not guarantee title, possession, outstanding dues, encumbrances
                or the accuracy of any detail. Every auction is governed solely by the terms of its official sale notice.
                Please read the official notice, inspect the property and verify all documents independently before bidding.
                EBG Realty can assist with legal due diligence and the bidding process on request.
            </p>
        </div>
    );
}

export default function AuctionDetail({ prop }) {
    const [active, setActive] = useState(0);
    const [lb, setLb] = useState(null);
    const closed = prop.auction_status === "closed";
    const gallery = prop.images?.length ? prop.images : [prop.cover_image];
    const enquiryMsg = `Hi EBG Realty, I'm interested in the auction property "${prop.title}" (${prop.locality}, ${prop.city}). Please help me with the bidding process.`;

    const facts = [
        { Icon: CalendarDays, l: "Auction Date",     v: fmtDate(prop.auction_date), k: "date" },
        { Icon: Clock,        l: "Auction Time",     v: prop.auction_time, k: "time" },
        { Icon: Wallet,       l: "EMD Amount",       v: prop.auction_emd, k: "emd" },
        { Icon: CalendarDays, l: "EMD Last Date",    v: prop.auction_emd_deadline && fmtDate(prop.auction_emd_deadline), k: "emd-deadline" },
        { Icon: Eye,          l: "Inspection Date",  v: prop.auction_inspection_date && fmtDate(prop.auction_inspection_date), k: "inspection" },
        { Icon: Landmark,     l: "Bank / Authority", v: prop.auction_authority, k: "authority" },
        { Icon: Gavel,        l: "Auction Type",     v: prop.auction_type, k: "type" },
        { Icon: KeyRound,     l: "Possession",       v: prop.auction_possession, k: "possession" },
        { Icon: FileText,     l: "Notice Ref.",      v: prop.auction_notice_ref, k: "notice" },
    ].filter((f) => f.v);

    return (
        <PageShell navVariant="dark">
            <div data-testid="auction-detail">
                <section className="bg-[#0a0d18] text-white pt-24 pb-12">
                    <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                        <nav className="text-[11px] text-white/50 mb-6 flex items-center gap-2 flex-wrap" data-testid="auction-breadcrumb">
                            <Link to="/" className="hover:text-[var(--ebg-gold)]">Home</Link><span>›</span>
                            <Link to="/auction-properties" className="hover:text-[var(--ebg-gold)]">Auction Properties</Link><span>›</span>
                            <span className="text-white">{prop.title}</span>
                        </nav>
                        <div className="grid lg:grid-cols-12 gap-8">
                            <div className="lg:col-span-7">
                                <button type="button" onClick={() => setLb(active)} className="relative block w-full aspect-[16/10] bg-black overflow-hidden border border-white/10 cursor-zoom-in" data-testid="auction-gallery-main">
                                    <img src={gallery[active]} alt={prop.title} className={`w-full h-full object-cover ${closed ? "grayscale-[60%]" : ""}`}/>
                                    <div className="absolute top-4 left-4 flex gap-2"><AuctionStatusPill item={prop}/><SampleTag show={prop.is_sample}/></div>
                                </button>
                                {gallery.length > 1 && (
                                    <div className="mt-3 grid grid-cols-6 gap-2">
                                        {gallery.slice(0, 6).map((im, i) => (
                                            <button key={i} onClick={() => setActive(i)} data-testid={`auction-thumb-${i}`} className={`aspect-[16/10] overflow-hidden border ${active === i ? "border-[var(--ebg-gold)]" : "border-white/10"}`}>
                                                <img src={im} alt="" className="w-full h-full object-cover"/>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="lg:col-span-5">
                                <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)] inline-flex items-center gap-2"><Gavel size={12}/> {prop.auction_type || "Auction Property"}</div>
                                <h1 className="font-serif text-4xl md:text-5xl leading-[1.05] mt-3" data-testid="auction-title">{prop.title}</h1>
                                <div className="flex items-center gap-2 mt-3 text-white/75 text-sm"><MapPin size={14} className="text-[var(--ebg-gold)]"/>{prop.locality}, {prop.city}</div>
                                <div className="mt-8">
                                    <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/50">Reserve Price</div>
                                    <div className="font-serif text-4xl md:text-5xl text-[var(--ebg-gold)] mt-1" data-testid="auction-reserve-price">{prop.price_display}</div>
                                    {prop.auction_emd && <div className="text-white/70 mt-1 text-sm">Earnest Money Deposit: <b className="text-white">{prop.auction_emd}</b></div>}
                                </div>
                                <div className="grid grid-cols-3 gap-3 mt-6 pt-6 border-t border-white/10 text-[13px]">
                                    <Stat Icon={Ruler} v={prop.area_sqft ? `${prop.area_sqft.toLocaleString()} sq.ft.` : "—"} l="Area"/>
                                    <Stat Icon={BedDouble} v={prop.bhk ? `${prop.bhk} BHK` : prop.type} l="Config"/>
                                    <Stat Icon={CalendarDays} v={fmtDate(prop.auction_date)} l="Auction"/>
                                </div>
                                <EnquiryCtas closed={closed} msg={enquiryMsg}/>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="max-w-[1440px] mx-auto px-6 md:px-10 py-12 pb-28 grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-8 space-y-12">
                        <div data-testid="auction-facts">
                            <h2 className="font-serif text-3xl md:text-4xl">Auction details</h2>
                            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-px bg-black/10 border border-black/10 mt-6">
                                {facts.map(({ Icon, l, v, k }) => (
                                    <div key={k} className="bg-white p-5" data-testid={`auction-fact-${k}`}>
                                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-black/45"><Icon size={12} className="text-[var(--ebg-gold)]"/>{l}</div>
                                        <div className="font-bold mt-2 text-[15px]">{v}</div>
                                    </div>
                                ))}
                            </div>
                            {prop.auction_source_url && (
                                <a href={prop.auction_source_url} target="_blank" rel="noopener noreferrer" data-testid="auction-source-link" className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--ebg-gold)] hover:text-black">
                                    View official auction notice <ExternalLink size={12}/>
                                </a>
                            )}
                        </div>
                        <div>
                            <h2 className="font-serif text-3xl md:text-4xl">About this property</h2>
                            <p className="text-black/70 leading-relaxed mt-4" data-testid="auction-description">{prop.description}</p>
                            {prop.highlights?.length > 0 && (
                                <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                                    {prop.highlights.map((h, i) => (
                                        <li key={i} className="flex items-start gap-3 text-[14px] text-black/75"><Check size={14} className="text-[var(--ebg-gold)] mt-1 shrink-0"/>{h}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <AuctionDisclaimer/>
                    </div>
                    <aside className="lg:col-span-4">
                        <div className="sticky top-28 space-y-4">
                            {closed ? <ClosedNotice/> : <AuctionEnquiryForm prop={prop}/>}
                        </div>
                    </aside>
                </section>
            </div>
            <Lightbox images={gallery} startIndex={lb || 0} open={lb !== null} onClose={() => setLb(null)} alt={prop.title}/>
        </PageShell>
    );
}

function Stat({ Icon, v, l }) {
    return (
        <div>
            <Icon size={15} className="text-[var(--ebg-gold)]"/>
            <div className="font-bold mt-2">{v}</div>
            <div className="text-white/50 text-[11px] uppercase tracking-[0.16em]">{l}</div>
        </div>
    );
}

function EnquiryCtas({ closed, msg }) {
    if (closed) {
        return (
            <div className="mt-8 border border-white/15 bg-white/[0.04] p-4 flex items-start gap-3" data-testid="auction-closed-banner">
                <Lock size={16} className="text-[var(--ebg-gold)] mt-0.5 shrink-0"/>
                <div className="text-[13px] text-white/75">This auction has closed and is no longer accepting enquiries. <Link to="/auction-properties" className="text-[var(--ebg-gold)] font-bold">See upcoming auctions →</Link></div>
            </div>
        );
    }
    return (
        <div className="mt-8 grid grid-cols-2 gap-3">
            <a href={CONTACT.tel} data-testid="auction-call" className="bg-[var(--ebg-gold)] text-black px-5 py-4 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-white transition-colors inline-flex items-center justify-center gap-2"><PhoneCall size={13}/> Call EBG</a>
            <a href={waLink(msg)} target="_blank" rel="noopener noreferrer" data-testid="auction-whatsapp" className="border border-white/40 text-white px-5 py-4 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-emerald-600 hover:border-emerald-600 transition-colors inline-flex items-center justify-center gap-2"><MessageCircle size={13}/> WhatsApp</a>
        </div>
    );
}

function ClosedNotice() {
    return (
        <div className="bg-white border border-black/8 p-6" data-testid="auction-closed-sidebar">
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-black/45">Status</div>
            <div className="font-serif text-2xl mt-2">Auction Closed</div>
            <p className="text-[13px] text-black/60 mt-2 leading-relaxed">Enquiries are closed for this listing. Browse live auctions or speak to our team about upcoming notices.</p>
            <Link to="/auction-properties" data-testid="auction-closed-browse" className="mt-5 w-full bg-black text-white py-3 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[var(--ebg-gold)] hover:text-black transition-colors inline-flex justify-center">Browse Upcoming Auctions</Link>
        </div>
    );
}

function AuctionEnquiryForm({ prop }) {
    const [form, setForm] = useState({ name: "", phone: "", email: "" });
    const [sending, setSending] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await api.post("/leads", { ...form, source: "auction_enquiry", property_id: prop.id, property_title: prop.title, message: `Auction enquiry — ${prop.title} (auction ${prop.auction_date || "TBA"})` });
            toast.success("Thank you. Our auction desk will call you shortly.");
            setForm({ name: "", phone: "", email: "" });
        } catch (err) { toast.error(formatApiError(err)); } finally { setSending(false); }
    };
    const input = "w-full border border-black/15 px-3 py-2.5 text-sm outline-none focus:border-[var(--ebg-gold)]";
    return (
        <form onSubmit={submit} className="bg-white border border-black/8 p-6 space-y-3" data-testid="auction-enquiry-form">
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--ebg-gold)]">EBG Auction Desk</div>
            <div className="font-serif text-2xl">Get help with this auction</div>
            <p className="text-[12px] text-black/55">Notice review, legal checks and bid registration — all enquiries are handled by EBG Realty.</p>
            <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="auction-form-name" className={input}/>
            <input required placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="auction-form-phone" className={input}/>
            <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="auction-form-email" className={input}/>
            <button disabled={sending} type="submit" data-testid="auction-form-submit" className="w-full bg-[var(--ebg-gold)] text-black py-3 text-[11px] uppercase tracking-[0.22em] font-bold disabled:opacity-60 hover:bg-black hover:text-[var(--ebg-gold)] transition-colors">
                {sending ? "Sending…" : "Request Call Back"}
            </button>
            <div className="text-[11px] text-black/50 text-center">or call <a href={CONTACT.tel} className="font-bold text-black">{CONTACT.display}</a></div>
        </form>
    );
}
